import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';
import { AddressAutocomplete } from '../components/AddressAutocomplete';
import { 
  Search, 
  MapPin, 
  Clock, 
  Star, 
  Truck, 
  Shield, 
  Smartphone,
  ChevronRight,
  ArrowRight,
  Users,
  Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

export default function LandingPage() {
  const { t } = useTranslation();
  const { api } = useAuth();
  const navigate = useNavigate();
  const [address, setAddress] = useState('');
  const [coordinates, setCoordinates] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gettingLocation, setGettingLocation] = useState(false);

  useEffect(() => {
    fetchRestaurants();
    // Try to get user's location on mount
    getUserLocation();
  }, []);

  const getUserLocation = () => {
    if (!navigator.geolocation) {
      return;
    }
    
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        setCoordinates({ lat: latitude, lng: longitude });
        
        // Reverse geocode to get address
        try {
          const response = await api.get(`/geocode/reverse?lat=${latitude}&lng=${longitude}`);
          if (response.data?.address) {
            setAddress(response.data.address);
          }
        } catch (error) {
          console.log('Could not reverse geocode location');
        }
        setGettingLocation(false);
      },
      (error) => {
        console.log('Location access denied or unavailable');
        setGettingLocation(false);
      },
      { timeout: 10000, enableHighAccuracy: false }
    );
  };

  const fetchRestaurants = async () => {
    try {
      const response = await api.get('/restaurants');
      setRestaurants(response.data.slice(0, 6));
    } catch (error) {
      console.error('Failed to fetch restaurants:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddressSelect = (selectedAddress) => {
    if (selectedAddress) {
      setAddress(selectedAddress.address || '');
      setCoordinates({
        lat: selectedAddress.latitude,
        lng: selectedAddress.longitude
      });
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (!address.trim()) {
      toast.error('Please enter your delivery address');
      return;
    }
    
    const params = new URLSearchParams();
    params.set('address', address);
    if (coordinates) {
      params.set('lat', coordinates.lat);
      params.set('lng', coordinates.lng);
    }
    navigate(`/restaurants?${params.toString()}`);
  };

  const features = [
    {
      icon: <Truck className="w-6 h-6" />,
      titleKey: "features.fastDelivery",
      descKey: "features.fastDeliveryDesc"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      titleKey: "features.safeSecure",
      descKey: "features.safeSecureDesc"
    },
    {
      icon: <Smartphone className="w-6 h-6" />,
      titleKey: "features.realTimeTracking",
      descKey: "features.realTimeTrackingDesc"
    }
  ];

  const cuisines = [
    { key: "italian", name: "Italian", image: "https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=200&h=200&fit=crop" },
    { key: "chinese", name: "Chinese", image: "https://images.unsplash.com/photo-1585032226651-759b368d7246?w=200&h=200&fit=crop" },
    { key: "mexican", name: "Mexican", image: "https://images.unsplash.com/photo-1565299585323-38d6b0865b47?w=200&h=200&fit=crop" },
    { key: "indian", name: "Indian", image: "https://images.unsplash.com/photo-1585937421612-70a008356fbe?w=200&h=200&fit=crop" },
    { key: "japanese", name: "Japanese", image: "https://images.unsplash.com/photo-1579871494447-9811cf80d66c?w=200&h=200&fit=crop" },
    { key: "american", name: "American", image: "https://images.unsplash.com/photo-1550547660-d9450f859349?w=200&h=200&fit=crop" },
  ];

  return (
    <>
      <SEO page="home" />
      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="gradient-hero pt-24 pb-20 lg:pt-32 lg:pb-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left content */}
              <div className="animate-fade-in">
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                  {t('hero.title')}
              </h1>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed max-w-lg">
                {t('hero.subtitle')}
              </p>
              
              {/* Search form */}
              <form onSubmit={handleSearch} className="relative max-w-xl" data-testid="hero-search-form">
                <div className="flex gap-2 p-2 bg-white rounded-full shadow-xl shadow-orange-500/10 border border-slate-100">
                  <div className="flex-1 flex items-center gap-2 px-2">
                    {gettingLocation ? (
                      <Loader2 className="w-5 h-5 text-orange-500 animate-spin" />
                    ) : (
                      <MapPin className="w-5 h-5 text-orange-500 flex-shrink-0" />
                    )}
                    <div className="flex-1">
                      <AddressAutocomplete
                        value={address}
                        onChange={setAddress}
                        onSelect={handleAddressSelect}
                        placeholder={gettingLocation ? "Getting your location..." : t('hero.search')}
                        className="border-0 focus:ring-0 shadow-none bg-transparent"
                        inputClassName="border-0 focus:ring-0 p-0 text-slate-900 placeholder:text-slate-400 bg-transparent"
                        data-testid="address-input"
                      />
                    </div>
                  </div>
                  <Button type="submit" className="btn-primary rounded-full px-6" data-testid="search-button">
                    <Search className="w-5 h-5 mr-2" />
                    {t('common.search')}
                  </Button>
                </div>
              </form>

              {/* Stats */}
              <div className="flex gap-8 mt-10">
                <div>
                  <div className="text-3xl font-bold text-slate-900">500+</div>
                  <div className="text-sm text-slate-500">{t('stats.restaurants')}</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-slate-900">50k+</div>
                  <div className="text-sm text-slate-500">{t('stats.happyCustomers')}</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-slate-900">30min</div>
                  <div className="text-sm text-slate-500">{t('stats.avgDelivery')}</div>
                </div>
              </div>
            </div>

            {/* Right image */}
            <div className="relative animate-fade-in stagger-2">
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1550547660-d9450f859349?w=600&h=500&fit=crop"
                  alt="Delicious burger"
                  className="rounded-3xl shadow-2xl shadow-orange-500/20 w-full object-cover"
                />
                {/* Floating card */}
                <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-lg animate-pulse-soft">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                      <Clock className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-slate-900">{t('features.fastDelivery')}</div>
                      <div className="text-xs text-slate-500">20-30 {t('time.minutes')}</div>
                    </div>
                  </div>
                </div>
                {/* Rating card */}
                <div className="absolute -top-4 -right-4 bg-white rounded-2xl p-4 shadow-lg">
                  <div className="flex items-center gap-2">
                    <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                    <span className="font-bold text-slate-900">4.9</span>
                    <span className="text-sm text-slate-500">(2.5k reviews)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cuisines Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900">{t('landing.browseByCuisine')}</h2>
            <Link to="/restaurants" className="text-orange-500 font-medium flex items-center gap-1 hover:gap-2 transition-all">
              {t('landing.viewAll')} <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
            {cuisines.map((cuisine, index) => (
              <Link
                key={cuisine.name}
                to={`/restaurants?cuisine=${cuisine.name}`}
                className="group text-center animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`cuisine-${cuisine.name.toLowerCase()}`}
              >
                <div className="relative w-20 h-20 mx-auto mb-3 rounded-full overflow-hidden border-4 border-transparent group-hover:border-orange-500 transition-all">
                  <img src={cuisine.image} alt={cuisine.name} className="w-full h-full object-cover" />
                </div>
                <span className="text-sm font-medium text-slate-700 group-hover:text-orange-500 transition-colors">
                  {t(`cuisine.${cuisine.key}`)}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Restaurants */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900">{t('landing.popularRestaurants')}</h2>
            <Link to="/restaurants" className="text-orange-500 font-medium flex items-center gap-1 hover:gap-2 transition-all">
              {t('landing.viewAll')} <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-2xl p-4 animate-pulse">
                  <div className="w-full h-48 bg-slate-200 rounded-xl mb-4"></div>
                  <div className="h-6 bg-slate-200 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-slate-200 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          ) : restaurants.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {restaurants.map((restaurant, index) => (
                <Link
                  key={restaurant.id}
                  to={`/restaurant/${restaurant.id}`}
                  className="card-restaurant animate-fade-in"
                  style={{ animationDelay: `${index * 0.1}s` }}
                  data-testid={`restaurant-card-${restaurant.id}`}
                >
                  <div className="relative">
                    <img
                      src={restaurant.image_url || `https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=400&h=250&fit=crop`}
                      alt={restaurant.name}
                      className="w-full h-48 object-cover"
                    />
                    {restaurant.delivery_enabled && (
                      <span className="absolute top-3 left-3 bg-green-500 text-white text-xs font-medium px-2 py-1 rounded-full">
                        {t('landing.freeDelivery')}
                      </span>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-lg text-slate-900 mb-1">{restaurant.name}</h3>
                    <p className="text-sm text-slate-500 mb-3">{restaurant.cuisine_type?.join(', ') || t('cuisine.various')}</p>
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                        <span className="font-medium text-slate-700">{restaurant.rating?.toFixed(1) || '4.5'}</span>
                      </div>
                      <div className="flex items-center gap-1 text-slate-500">
                        <Clock className="w-4 h-4" />
                        <span>25-35 {t('time.minutes')}</span>
                      </div>
                      <span className="text-slate-500">
                        {t('restaurant.minOrder')} £{restaurant.minimum_order || 10}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-slate-500 mb-4">{t('landing.noRestaurants')}</p>
              <Link to="/register?role=restaurant_admin">
                <Button className="btn-primary">{t('landing.registerRestaurant')}</Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{t('landing.whyChoose')}</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {t('landing.whyChooseSubtitle')}
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={feature.titleKey} 
                className="text-center p-8 rounded-2xl bg-slate-50 hover:bg-orange-50 transition-colors animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-orange-100 flex items-center justify-center text-orange-500">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">{t(feature.titleKey)}</h3>
                <p className="text-slate-600">{t(feature.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 gradient-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{t('landing.readyToOrder')}</h2>
          <p className="text-orange-100 text-lg mb-8">
            {t('landing.joinCommunity')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register" data-testid="cta-register">
              <Button className="bg-white text-orange-500 hover:bg-orange-50 rounded-full px-8 py-6 font-semibold shadow-lg">
                {t('auth.register')} <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/restaurants" data-testid="cta-browse">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-8 py-6 font-semibold">
                {t('nav.restaurants')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Work With Us Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{t('workWithUs.title')}</h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              {t('workWithUs.subtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Become a Driver */}
            <Link 
              to="/apply/driver"
              className="group bg-slate-800 rounded-2xl p-8 hover:bg-slate-700 transition-colors"
              data-testid="apply-driver-link"
            >
              <div className="w-16 h-16 rounded-full bg-orange-500/20 flex items-center justify-center mb-6 group-hover:bg-orange-500/30 transition-colors">
                <Truck className="w-8 h-8 text-orange-500" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">{t('workWithUs.becomeDriver')}</h3>
              <p className="text-slate-400 mb-4">
                {t('workWithUs.driverDesc')}
              </p>
              <ul className="text-sm text-slate-400 space-y-2 mb-6">
                <li>✓ {t('workWithUs.driverBenefit1')}</li>
                <li>✓ {t('workWithUs.driverBenefit2')}</li>
                <li>✓ {t('workWithUs.driverBenefit3')}</li>
              </ul>
              <span className="inline-flex items-center text-orange-500 font-semibold group-hover:gap-2 transition-all">
                {t('workWithUs.applyNow')} <ArrowRight className="w-5 h-5 ml-1" />
              </span>
            </Link>

            {/* Become a Salesperson */}
            <Link 
              to="/apply/salesperson"
              className="group bg-slate-800 rounded-2xl p-8 hover:bg-slate-700 transition-colors"
              data-testid="apply-salesperson-link"
            >
              <div className="w-16 h-16 rounded-full bg-blue-500/20 flex items-center justify-center mb-6 group-hover:bg-blue-500/30 transition-colors">
                <Users className="w-8 h-8 text-blue-500" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">{t('workWithUs.joinSales')}</h3>
              <p className="text-slate-400 mb-4">
                {t('workWithUs.salesDesc')}
              </p>
              <ul className="text-sm text-slate-400 space-y-2 mb-6">
                <li>✓ {t('workWithUs.salesBenefit1')}</li>
                <li>✓ {t('workWithUs.salesBenefit2')}</li>
                <li>✓ {t('workWithUs.salesBenefit3')}</li>
              </ul>
              <span className="inline-flex items-center text-blue-500 font-semibold group-hover:gap-2 transition-all">
                {t('workWithUs.applyNow')} <ArrowRight className="w-5 h-5 ml-1" />
              </span>
            </Link>
          </div>
        </div>
      </section>
      </div>
    </>
  );
}
